import { useGetCredentialsQuery } from '../../../store/modules/merchant/api';
import type { ProvisioningPlatform } from './paths';
import { useBigCommerceProvisioningEligibility } from './bigcommerce/useBigCommerceProvisioningEligibility';
import { useShopifyProvisioningEligibility } from './shopify/useShopifyProvisioningEligibility';
import { findSquarespaceCredential } from './squarespace/squarespaceProvisioning';

/**
 * The merchant's provisioning checklist, if any. Shopify and BigCommerce by externalPlatformId;
 * Squarespace by default ecommerce credential.
 */
export const useProvisioningPlatform = (): {
  platform: ProvisioningPlatform | null;
  isLoading: boolean;
  /** Whether going live needs provisioning complete. True while loading. */
  requiresProvisioning: boolean;
} => {
  const {
    merchantId,
    isEligible: isShopify,
    isLoading: isShopifyLoading,
  } = useShopifyProvisioningEligibility();
  const { isEligible: isBigCommerce, isLoading: isBigCommerceLoading } =
    useBigCommerceProvisioningEligibility();
  const {
    currentData: credentials,
    isUninitialized: areCredentialsUninitialized,
    isLoading: areCredentialsLoading,
    isFetching: areCredentialsFetching,
  } = useGetCredentialsQuery(
    { merchantId: merchantId ?? '', type: ['ecommerce'] },
    { skip: !merchantId || isShopify },
  );
  // `data` / `isLoading` stay on the previous cache entry after merchantId changes.
  const credentialsPending =
    Boolean(merchantId) &&
    !isShopify &&
    (areCredentialsUninitialized ||
      areCredentialsLoading ||
      (areCredentialsFetching && credentials === undefined));

  if (isShopify) {
    return { platform: 'shopify', isLoading: false, requiresProvisioning: true };
  }

  // Hash-shaped externalPlatformId is not unique; wait for this merchant's credentials so Squarespace wins.
  if (isBigCommerce && !credentialsPending) {
    if (findSquarespaceCredential(credentials)) {
      return { platform: 'squarespace', isLoading: false, requiresProvisioning: true };
    }

    return { platform: 'bigcommerce', isLoading: false, requiresProvisioning: true };
  }

  const platform = findSquarespaceCredential(credentials) ? 'squarespace' : null;
  const isLoading = isShopifyLoading || isBigCommerceLoading || credentialsPending;
  return { platform, isLoading, requiresProvisioning: isLoading || platform !== null };
};
